import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, take } from 'rxjs';
import { selectDataByKey } from 'src/storage/general.selectors';
import { MembershipService } from '@app/services/pagos/membership.service';
import { MembershipResponse, MembershipType } from '@app/interfaces/membership.interface';
import { CardDataService } from 'src/app/sarlaft/services/card-data.service';
import {
  PAGO_APROBADO_NO_AP,
  PAGO_RECHAZADO_P2P,
  POLIZA_ACTIVA,
  PROCESANDO_PAGO,
  TENEMOS_TUS_DATOS,
} from '@app/routes.constants';

// [DRAFT] Pantalla intermedia P2P — UI pendiente de validación por UX/UI. En producción NO se usa: el
// lightbox se abre directamente desde sandriaft-page. Se conserva la ruta y el componente para retomar
// esta iteración cuando UX apruebe el diseño (fase 2 del feature P2P).

@Component({
  selector: 'app-resumen-compra-p2p',
  templateUrl: './resumen-compra-p2p.component.html',
  styleUrl: './resumen-compra-p2p.component.scss',
})
export class ResumenCompraP2PComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly membershipService = inject(MembershipService);
  private readonly cardDataService = inject(CardDataService);

  private readonly subs = new Subscription();

  // Fuente principal de datos del resumen: mismo endpoint que consume sarlaft (getResumeDataPago=true).
  readonly cardData = signal<any>(null);

  readonly sessionId = signal('');
  readonly quoteId = signal('');
  // QuoteId de Salesforce (0Q0U...); es el recordId que exige membresiaservice, distinto del prepolicyid encriptado.
  readonly salesforceQuoteId = signal('');
  readonly codAsesor = signal('');

  readonly property = toSignal(this.store.select(selectDataByKey('Property')), { initialValue: null });
  readonly tenant = toSignal(this.store.select(selectDataByKey('Tenant')), { initialValue: null });
  readonly policyHolder = toSignal(this.store.select(selectDataByKey('PolicyHolder')), { initialValue: null });
  // En arrendamiento el plan seleccionado se persiste en la key 'Plan' (savePlanToStore de new-planes-seguros).
  readonly plan = toSignal(this.store.select(selectDataByKey('Plan')), { initialValue: null });
  readonly productCode = toSignal(this.store.select(selectDataByKey('ProductCode')), { initialValue: null });

  readonly isLoading = signal(false);
  readonly isPaying = signal(false);
  readonly showPromo = signal(true);
  readonly promoPoints = signal<number>(2000);
  readonly pointsPerAmount = signal<number>(800);
  readonly selectedPaymentMethod = signal<MembershipType | null>(null);

  readonly selectedPlan = computed<any>(() => this.plan());

  readonly planTitle = computed<string>(() => {
    const cd: any = this.cardData();
    const p: any = this.selectedPlan();
    return cd?.planName || p?.planName || p?.name || p?.code || this.productCode() || 'Seguro de arrendamiento';
  });

  // cardData.coverages viene como string[]; fallback al shape del store si estuviera hidratado.
  readonly coverages = computed(() => {
    const cd: any = this.cardData();
    if (Array.isArray(cd?.coverages) && cd.coverages.length) {
      return cd.coverages.map((name: string) => ({ name: String(name), include: true }));
    }
    const p: any = this.selectedPlan();
    const list: any[] = p?.coverages ?? p?.coveragesGroup ?? [];
    return list.map((c) => ({
      name: c?.name || '',
      include: c?.include !== false && c?.isIncluded !== false,
    }));
  });

  readonly tipoPago = computed<'Mensual' | 'Anual'>(() => {
    const cd: any = this.cardData();
    const plan: any = this.selectedPlan();
    const freq: string | undefined =
      cd?.payment_frequency || cd?.PaymentFrequency || plan?.payment_frequency || plan?.paymentFrequency;
    return freq === 'Monthly' || freq === 'Mensual' ? 'Mensual' : 'Anual';
  });

  readonly valorMes = computed<number>(() => {
    const cd: any = this.cardData();
    const p: any = this.selectedPlan();
    return this.toNumber(
      cd?.priceMensual?.amount ?? cd?.MonthlyAmount ??
      p?.priceMensual?.amount ?? p?.price?.price_monthly?.amount ?? p?.priceMensual ?? p?.planEsencial ?? 0,
    );
  });

  readonly valorAnual = computed<number>(() => {
    const cd: any = this.cardData();
    const p: any = this.selectedPlan();
    return this.toNumber(
      cd?.totalAmount ?? cd?.priceAnual?.amount ??
      p?.priceAnual?.amount ?? p?.price?.full?.amount ?? p?.price ?? p?.planPlus ?? p?.armaTuPlan ?? 0,
    );
  });

  readonly valorAPagar = computed<number>(() =>
    this.tipoPago() === 'Mensual' ? this.valorMes() : this.valorAnual(),
  );

  private toNumber(v: any): number {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      const n = Number(v.replace(/[^\d.-]/g, ''));
      return isNaN(n) ? 0 : n;
    }
    return 0;
  }

  readonly propertyAddress = computed<string>(() => {
    const cd: any = this.cardData();
    const p: any = this.property();
    return cd?.PropertyAddress || p?.PropertyAddress || p?.address || p?.direccion || '';
  });

  readonly propertyCity = computed<string>(() => {
    const cd: any = this.cardData();
    const p: any = this.property();
    return cd?.PropertyCity || p?.CityProperty || p?.city || p?.ciudad || '';
  });

  readonly propertyRegistration = computed<string>(() => {
    const p: any = this.property();
    return p?.RealEstateLicensePlate || p?.matricula || '';
  });

  readonly contractStartDate = computed<Date>(() => {
    const cd: any = this.cardData();
    const p: any = this.property();
    const raw: string | undefined = cd?.ContractStartDate || p?.ContractStartDate;
    if (raw) {
      // cardData entrega dd/MM/yyyy; el store puede traer yyyy-MM-dd (ISO).
      const dmy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (dmy) return new Date(+dmy[3], +dmy[2] - 1, +dmy[1]);
      const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      const d = ymd ? new Date(+ymd[1], +ymd[2] - 1, +ymd[3]) : new Date(raw);
      if (!isNaN(d.getTime())) return d;
    }
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Bogota' }));
  });

  readonly ownerInfo = computed(() => {
    const cd: any = this.cardData();
    // cardData.name/DocumentNumber ya vienen enmascarados por CardDataService.shadow().
    if (cd?.name || cd?.DocumentNumber) {
      return {
        fullname: cd.name || 'No disponible',
        document: { type: '', number: cd.DocumentNumber || '', masked: cd.DocumentNumber || '' },
      };
    }
    const ph: any = this.policyHolder();
    if (!ph) return null;
    const fullname = [ph.FirstName, ph.SecondName, ph.LastName, ph.SecondLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const number = String(ph.DocumentNumber || '');
    return {
      fullname: fullname || 'No disponible',
      document: {
        type: ph.DocumentType || '',
        number,
        masked: this.maskDocument(number),
      },
    };
  });

  // Mantiene el primer dígito visible y enmascara el resto (paridad con resumen legacy).
  private maskDocument(value: string): string {
    if (!value) return '';
    if (value.length <= 1) return value;
    return value[0] + '*'.repeat(value.length - 1);
  }

  readonly tenantInfo = computed(() => {
    const t: any = this.tenant();
    if (!t) return null;
    const fullname = [t.FirstName, t.SecondName, t.LastName, t.SecondLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    return {
      fullname: fullname || 'No disponible',
      document: { type: t.DocumentType || '', number: t.DocumentNumber || '' },
    };
  });

  ngOnInit(): void {
    this.sessionId.set(this.readSessionId());

    this.subs.add(
      this.route.queryParams.subscribe((p) => {
        this.codAsesor.set(p['codigoAsesor'] || '');
        if (p['quoteId']) {
          this.salesforceQuoteId.set(String(p['quoteId']));
          try { sessionStorage.setItem('sfQuoteId', String(p['quoteId'])); } catch {}
        } else {
          // Fallback a sessionStorage: sarlaft-page lo guarda ahí tras el payService.
          const stored = (() => { try { return sessionStorage.getItem('sfQuoteId') || ''; } catch { return ''; } })();
          if (stored) {
            this.salesforceQuoteId.set(stored);
          }
        }
        if (p['prepolicyid']) {
          // El quoteId de sarlaft viene URL-encoded (%2F, %2B, %3D); membresiaservice espera el ID raw.
          const decoded = this.safeDecode(String(p['prepolicyid']));
          this.quoteId.set(decoded);
          this.loadCardData();
        }
      }),
    );

    this.subs.add(
      this.store.select(selectDataByKey('quoteId')).pipe(take(1)).subscribe((v) => {
        if (!this.quoteId() && v) {
          this.quoteId.set(String(v));
          this.loadCardData();
        }
      }),
    );
  }

  private loadCardData(): void {
    const id = this.quoteId();
    if (!id || this.cardData()) return;
    this.isLoading.set(true);
    this.subs.add(
      this.cardDataService.fetchInitialData(id, true).subscribe({
        next: (data) => {
          this.cardData.set(data);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('[ResumenCompraP2P] Error cargando cardData:', err);
          this.isLoading.set(false);
        },
      }),
    );
  }

  private safeDecode(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  selectMethod(method: MembershipType): void {
    if (this.isPaying()) return;
    this.selectedPaymentMethod.set(method);
  }

  handlePayment(type: MembershipType): void {
    if (this.isPaying()) return;

    // membresiaservice exige el QuoteId de Salesforce (0Q0U...), no el ID encriptado del sarlaft.
    const recordId = this.salesforceQuoteId() || this.quoteId();
    if (!recordId) {
      console.error('[ResumenCompraP2P] No se encontro recordId para procesar el pago.');
      return;
    }

    this.isPaying.set(true);

    this.membershipService
      .getLightboxUrl(this.sessionId(), recordId, type, 'Arrendamientos')
      .subscribe({
        next: (response: MembershipResponse) => {
          this.handleMembershipResponse(response);
        },
        error: (err) => {
          console.error('[ResumenCompraP2P] Error al obtener la URL de pago:', err);
          this.redirect(TENEMOS_TUS_DATOS);
        },
      });
  }

  private handleMembershipResponse(response: MembershipResponse): void {
    const status = response?.data?.status?.status;
    const message = response?.data?.status?.message;

    if (status === 'PENDING' && message === 'Se encuentra un pago pentiente para otra cotización') {
      this.redirect(PAGO_RECHAZADO_P2P);
      return;
    }

    if (status === 'APPROVED') {
      if (message === 'Hay una sesión aprobada que tiene póliza Vigente.') {
        this.redirect(POLIZA_ACTIVA);
      } else {
        this.redirect(PAGO_APROBADO_NO_AP);
      }
      return;
    }

    if (response?.data?.processUrl) {
      const P = (window as any).P;
      if (!P) {
        console.error('[ResumenCompraP2P] Lightbox de Place to Pay no disponible en window.P');
        this.isPaying.set(false);
        return;
      }
      P.init(response.data.processUrl);
      P.on('close', () => this.redirect(PROCESANDO_PAGO));
      return;
    }

    console.error('[ResumenCompraP2P] Respuesta inesperada del servicio de pago:', response);
    this.isPaying.set(false);
  }

  private redirect(url: string): void {
    this.isPaying.set(false);
    const queryParams: Record<string, string> = { prepolicyid: this.quoteId() };
    if (this.salesforceQuoteId()) queryParams['quoteId'] = this.salesforceQuoteId();
    if (this.codAsesor()) queryParams['codigoAsesor'] = this.codAsesor();
    // Las constantes de routes.constants quedan sin prefijo en dev (BASE=''); forzamos absoluto para matchear las rutas registradas.
    const target = url.startsWith('/') ? url : `/seguros/hogar/arriendo/cotizador/${url}`;
    this.router.navigate([target], { queryParams }).then(() => {
      setTimeout(() => window.scrollTo(0, 0), 0);
    });
  }

  private readSessionId(): string {
    return String(localStorage.getItem('session_id') || sessionStorage.getItem('session_id') || '');
  }
}
